import React, {useEffect, useState, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {doc, collection, query, orderBy, onSnapshot, Timestamp} from 'firebase/firestore';
import {db} from '../config/firebase';
import ProgressSubMessage from './shared/ProgressSubMessage';

type StageEntry = {key: string; label: string};

const DEFAULT_STAGES: StageEntry[] = [
  {key: 'QUEUED', label: 'Queued'},
  {key: 'VALIDATING_INPUTS', label: 'Validating inputs'},
  {key: 'UPLOADING_AND_PARSING_FILES', label: 'Processing files'},
  {key: 'GENERATING_DRAFT_STRUCTURE', label: 'Generating structure'},
  {key: 'FILLING_STEP_DETAILS', label: 'Adding details'},
  {key: 'NORMALIZING_AND_VALIDATING', label: 'Validating'},
  {key: 'SAVING_DRAFT', label: 'Saving draft'},
  {key: 'FINALIZING', label: 'Finalizing'},
];

const STAGES_BY_TYPE: Record<string, StageEntry[]> = {
  GUIDE: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Validating inputs'},
    {key: 'UPLOADING_AND_PARSING_FILES', label: 'Processing attachments'},
    {key: 'GENERATING_DRAFT_STRUCTURE', label: 'Generating guide'},
    {key: 'SAVING_DRAFT', label: 'Saving guide'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  GROUP_GUIDE_GENERATION: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Validating inputs'},
    {key: 'UPLOADING_AND_PARSING_FILES', label: 'Reading source documents'},
    {key: 'GENERATING_DRAFT_STRUCTURE', label: 'Generating guide'},
    {key: 'SAVING_DRAFT', label: 'Saving guide'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  ANALYSIS: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Validating inputs'},
    {key: 'UPLOADING_AND_PARSING_FILES', label: 'Processing attachments'},
    {key: 'GENERATING_DRAFT_STRUCTURE', label: 'Analyzing goal'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  GROUP_SUGGESTION: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Loading source items'},
    {key: 'GENERATING_DRAFT_STRUCTURE', label: 'Clustering documents'},
    {key: 'FILLING_STEP_DETAILS', label: 'Scoring clusters'},
    {key: 'SAVING_DRAFT', label: 'Saving suggestions'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  SOURCE_TEXT_EXTRACTION: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Loading source items'},
    {key: 'UPLOADING_AND_PARSING_FILES', label: 'Extracting text'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  SOURCE_SUMMARIZATION: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Loading source items'},
    {key: 'UPLOADING_AND_PARSING_FILES', label: 'Summarizing documents'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  SOURCE_CLASSIFICATION: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Loading source items'},
    {key: 'UPLOADING_AND_PARSING_FILES', label: 'Classifying (wave 1)'},
    {key: 'GENERATING_DRAFT_STRUCTURE', label: 'Classifying (wave 2)'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  SOURCE_ANALYSIS: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Loading source items'},
    {key: 'ANALYZING', label: 'Analyzing documents'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  WEB_CRAWL: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Validating URL'},
    {key: 'CRAWLING_PAGES', label: 'Crawling pages'},
    {key: 'DOWNLOADING_DOCUMENTS', label: 'Downloading documents'},
    {key: 'IMPORTING_DOCUMENTS', label: 'Importing to library'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  LIBRARY_SCAN: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'VALIDATING_INPUTS', label: 'Loading source items'},
    {key: 'ANALYZING', label: 'Analyzing documents'},
    {key: 'UPLOADING_AND_PARSING_FILES', label: 'Extracting text'},
    {key: 'FINALIZING', label: 'Finalizing'},
  ],
  EVAL_RUN: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'RUNNING_EVAL', label: 'Evaluating prompt'},
  ],
  EVAL_GENERATE_AND_RUN: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'RUNNING_BASELINE_EVAL', label: 'Evaluating current prompt'},
    {key: 'GENERATING_PROMPT', label: 'Generating improved prompt'},
    {key: 'RUNNING_EVAL', label: 'Evaluating new prompt'},
  ],
  EVAL_MULTI_MODEL: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'RUNNING_EVAL', label: 'Evaluating models'},
    {key: 'FINALIZING', label: 'Saving results'},
  ],
  EVAL_ITERATE: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'RUNNING_BASELINE_EVAL', label: 'Evaluating current prompt'},
    {key: 'ITERATING', label: 'Generating & evaluating candidates'},
    {key: 'FINALIZING', label: 'Saving results'},
  ],
  EVAL_RERUN_REPORT: [
    {key: 'QUEUED', label: 'Queued'},
    {key: 'RUNNING_EVAL', label: 'Rerunning all cases'},
  ],
};

/**
 * Look up the human-readable label for a stage key given a job type.
 * Falls back to converting the raw key (e.g. GENERATING_DRAFT_STRUCTURE → "Generating draft structure").
 */
export function getStageLabelForType(stageKey: string, jobType?: string): string {
  const stages = (jobType && STAGES_BY_TYPE[jobType]) || DEFAULT_STAGES;
  const match = stages.find(s => s.key === stageKey);
  if (match) return match.label;
  // Fallback: humanize the raw key
  return stageKey.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

function getStagesForType(jobType?: string, scanTasks?: string[]): StageEntry[] {
  if (jobType === 'LIBRARY_SCAN' && scanTasks) {
    const hasAnalysis = scanTasks.includes('analysis') || scanTasks.includes('summarization') || scanTasks.includes('classification');
    const hasExtraction = scanTasks.includes('extraction');
    const stages: StageEntry[] = [{key: 'QUEUED', label: 'Queued'}, {key: 'VALIDATING_INPUTS', label: 'Loading source items'}];
    if (hasAnalysis) stages.push({key: 'ANALYZING', label: 'Analyzing documents'});
    if (hasExtraction) stages.push({key: 'UPLOADING_AND_PARSING_FILES', label: 'Extracting text'});
    stages.push({key: 'FINALIZING', label: 'Finalizing'});
    return stages;
  }
  if (jobType && STAGES_BY_TYPE[jobType]) return STAGES_BY_TYPE[jobType];
  return DEFAULT_STAGES;
}

const STRUCTURE_HINTS: Record<string, string[]> = {
  GUIDE: [
    'Creating phases for your plan...',
    'Defining roles and responsibilities...',
    'Mapping step dependencies...',
    'Estimating timelines...',
    'Identifying automation opportunities...',
  ],
  WEB_CRAWL: [
    'Discovering linked pages...',
    'Following links on the same domain...',
    'Looking for downloadable documents...',
  ],
  GROUP_SUGGESTION: [
    'Comparing document topics...',
    'Identifying clusters...',
    'Evaluating grouping quality...',
  ],
};

const DEFAULT_HINTS = [
  'Processing...',
  'Working on it...',
];

const HINT_SILENCE_THRESHOLD_MS = 15_000;
const HINT_ROTATE_INTERVAL_MS = 12_000;

interface JobDoc {
  type?: string;
  status: string;
  currentStage: string;
  resultRef?: string | null;
  errorMessage?: string | null;
  inputSnapshot?: {scanTasks?: string[]};
  inputSummary?: string | null;
  resultData?: {
    tokenUsage?: {input: number; output: number; total: number};
    costEstimate?: {totalCost: number; model: string; pricingDate: string} | null;
  } | null;
}

interface GenerationProgressProps {
  jobId: string;
  onComplete?: (resultRef: string) => void;
  onRetry?: () => void;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

export default function GenerationProgress({jobId, onComplete, onRetry}: GenerationProgressProps) {
  const [job, setJob] = useState<JobDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stageTimestamps, setStageTimestamps] = useState<Map<string, Date>>(new Map());
  const [stageMessages, setStageMessages] = useState<Map<string, string>>(new Map());
  const [stageTokens, setStageTokens] = useState<Map<string, number>>(new Map());
  const [now, setNow] = useState(Date.now());
  const [hintText, setHintText] = useState<string | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastServerMsgRef = useRef<number>(0);
  const hintIndexRef = useRef(0);
  const hintTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Subscribe to job document
  useEffect(() => {
    const docRef = doc(db, 'generation_jobs', jobId);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as JobDoc;
          setJob(data);

          if (data.status === 'SUCCEEDED' && data.resultRef && onComplete) {
            onComplete(data.resultRef);
          }
        } else {
          setError('Job not found');
        }
      },
      (err) => {
        console.error('Job snapshot error:', err);
        setError('Failed to connect to job');
      }
    );

    return () => unsubscribe();
  }, [jobId, onComplete]);

  // Subscribe to events subcollection for stage timestamps and messages
  useEffect(() => {
    const eventsRef = collection(db, 'generation_jobs', jobId, 'events');
    const q = query(eventsRef, orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const timestamps = new Map<string, Date>();
        const messages = new Map<string, string>();
        const tokens = new Map<string, number>();
        snapshot.docs.forEach((d) => {
          const data = d.data();
          if (data.stage && data.timestamp) {
            const ts = data.timestamp instanceof Timestamp
              ? data.timestamp.toDate()
              : new Date(data.timestamp);
            // Keep the first occurrence of each stage for timing
            if (!timestamps.has(data.stage)) {
              timestamps.set(data.stage, ts);
            }
            // Keep the latest message and tokens for each stage
            if (data.message) {
              messages.set(data.stage, data.message);
              if (data.stage === 'GENERATING_DRAFT_STRUCTURE') {
                lastServerMsgRef.current = Date.now();
              }
            }
            if (data.metadata?.tokens != null) {
              tokens.set(data.stage, data.metadata.tokens);
            }
          }
        });
        setStageTimestamps(timestamps);
        setStageMessages(messages);
        setStageTokens(tokens);
      },
      (err) => {
        // Non-critical — just won't show timing
        console.warn('Events snapshot error:', err);
      }
    );

    return () => unsubscribe();
  }, [jobId]);

  // Tick every second for elapsed time on active stage
  const isRunning = job && (job.status === 'QUEUED' || job.status === 'RUNNING');
  useEffect(() => {
    if (isRunning) {
      tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    } else if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [isRunning]);

  // Rotating hints during GENERATING_DRAFT_STRUCTURE
  const isStructureStage = job?.currentStage === 'GENERATING_DRAFT_STRUCTURE' && isRunning;
  const jobType = job?.type;

  const advanceHint = useCallback(() => {
    const sinceLast = Date.now() - lastServerMsgRef.current;
    if (sinceLast >= HINT_SILENCE_THRESHOLD_MS) {
      const hints = (jobType && STRUCTURE_HINTS[jobType]) || DEFAULT_HINTS;
      setHintText(hints[hintIndexRef.current % hints.length]);
      hintIndexRef.current += 1;
    }
  }, [jobType]);

  useEffect(() => {
    if (isStructureStage) {
      // Seed the timer reference so hints can start after silence threshold
      if (lastServerMsgRef.current === 0) {
        lastServerMsgRef.current = Date.now();
      }
      hintIndexRef.current = 0;
      setHintText(null);

      // Check immediately, then every HINT_ROTATE_INTERVAL_MS
      const initialTimeout = setTimeout(advanceHint, HINT_SILENCE_THRESHOLD_MS);
      hintTimerRef.current = setInterval(advanceHint, HINT_ROTATE_INTERVAL_MS);

      return () => {
        clearTimeout(initialTimeout);
        if (hintTimerRef.current) clearInterval(hintTimerRef.current);
        hintTimerRef.current = null;
      };
    } else {
      setHintText(null);
      if (hintTimerRef.current) {
        clearInterval(hintTimerRef.current);
        hintTimerRef.current = null;
      }
    }
  }, [isStructureStage, advanceHint]);

  // Server messages take priority over hints — reset hint when new server message arrives
  const serverMsg = stageMessages.get('GENERATING_DRAFT_STRUCTURE');
  useEffect(() => {
    if (serverMsg) {
      setHintText(null);
      hintIndexRef.current = 0;
    }
  }, [serverMsg]);

  if (error) {
    return (
      <Alert severity="error" sx={{mt: 2}}>
        {error}
      </Alert>
    );
  }

  if (!job) {
    return (
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 2}}>
        <CircularProgress size={20} />
        <Typography sx={{fontSize: 14, color: '#6B7280'}}>Connecting...</Typography>
      </Box>
    );
  }

  const stages = getStagesForType(job.type, job.inputSnapshot?.scanTasks);
  const activeStageIndex = stages.findIndex(s => s.key === job.currentStage);
  const isFailed = job.status === 'FAILED' || job.status === 'CANCELED';
  const isSucceeded = job.status === 'SUCCEEDED';

  // Build timing info for each stage
  function getStageTiming(stageKey: string, index: number): string | null {
    const stageStart = stageTimestamps.get(stageKey);
    if (!stageStart) return null;

    const isActive = index === activeStageIndex && !isFailed && !isSucceeded;
    const isCompleted = isSucceeded || index < activeStageIndex;
    const isFailedStage = isFailed && index === activeStageIndex;

    if (isActive) {
      // Elapsed time (ticking)
      const elapsed = now - stageStart.getTime();
      return formatDuration(Math.max(0, elapsed));
    }

    if (isCompleted || isFailedStage) {
      // Find the next stage's timestamp to compute duration
      const nextStage = stages[index + 1];
      const nextStart = nextStage ? stageTimestamps.get(nextStage.key) : null;
      if (nextStart) {
        return formatDuration(nextStart.getTime() - stageStart.getTime());
      }
      // Last completed stage — no next timestamp available
      return null;
    }

    return null;
  }

  // Determine sub-message for a given stage
  function getSubMessage(stageKey: string, isActive: boolean): {message: string; tokens?: number} | null {
    if (!isActive) return null;
    // Server message always takes priority
    const msg = stageMessages.get(stageKey);
    if (msg) return {message: msg, tokens: stageTokens.get(stageKey)};
    // Show inputSummary as progress (e.g. "Classifying 5/100...") — but not during QUEUED
    // and not if it looks like a raw prompt (long text without progress pattern)
    if (job?.inputSummary && stageKey !== 'QUEUED') {
      const summary = job.inputSummary;
      if (summary.length <= 120) return {message: summary};
      // Truncate long summaries (likely raw prompts from old jobs)
      return {message: summary.substring(0, 100) + '...'};
    }
    // Show rotating hint only for the structure stage
    if (stageKey === 'GENERATING_DRAFT_STRUCTURE' && hintText) return {message: hintText};
    return null;
  }

  return (
    <Box sx={{py: 2}}>
      <Stepper
        activeStep={isSucceeded ? stages.length : activeStageIndex}
        orientation="vertical"
        sx={{
          '& .MuiStepConnector-line': {minHeight: 16},
        }}
      >
        {stages.map((stage, index) => {
          const isActive = index === activeStageIndex && !isFailed && !isSucceeded;
          const isCompleted = isSucceeded || index < activeStageIndex;
          const timing = getStageTiming(stage.key, index);
          const subMessage = getSubMessage(stage.key, isActive);

          return (
            <Step key={stage.key} completed={isCompleted}>
              <StepLabel
                StepIconComponent={() => {
                  if (isCompleted) {
                    return <CheckCircleIcon sx={{fontSize: 20, color: '#10B981'}} />;
                  }
                  if (isActive) {
                    return <CircularProgress size={18} />;
                  }
                  if (isFailed && index === activeStageIndex) {
                    return <ErrorIcon sx={{fontSize: 20, color: '#EF4444'}} />;
                  }
                  return (
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: '2px solid #D1D5DB',
                      }}
                    />
                  );
                }}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? '#111827' : isCompleted ? '#6B7280' : '#9CA3AF',
                  },
                }}
              >
                <Box>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    {stage.label}
                    {timing && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 12,
                          color: isActive ? '#3B82F6' : '#9CA3AF',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {timing}
                      </Typography>
                    )}
                  </Box>
                  {subMessage && (
                    <Box sx={{mt: 0.25}}>
                      <ProgressSubMessage
                        message={subMessage.message}
                        tokens={subMessage.tokens}
                      />
                    </Box>
                  )}
                </Box>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {isFailed && (
        <Alert
          severity="error"
          sx={{mt: 2}}
          action={
            onRetry ? (
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {job.errorMessage || 'Job failed'}
        </Alert>
      )}

      {isSucceeded && (
        <Alert severity="success" sx={{mt: 2}}>
          Complete!{job.resultData?.tokenUsage?.total ? ` (${job.resultData.tokenUsage.total.toLocaleString()} tokens${job.resultData.costEstimate?.totalCost ? ` · ~$${job.resultData.costEstimate.totalCost < 0.01 ? job.resultData.costEstimate.totalCost.toFixed(4) : job.resultData.costEstimate.totalCost.toFixed(2)}` : ''})` : ''}
        </Alert>
      )}
    </Box>
  );
}
